import Cookies from 'js-cookie';
import axios from 'axios';
import { ElMessage } from 'element-plus';
import { onBeforeUnmount } from 'vue';
import { Toast } from 'vant';
import { loginError } from '@/lib/helpers/user';
import { filterUselessParams, isWechatBrowser } from '@/lib/utils';
import errReport from '@/lib/errReport';
import { checkVersion } from '@/router/checkVersion';
import { rootStore } from '@/store/rootStore';

// 全局请求取消控制器
const CancelToken = axios.CancelToken;
let reqNum = 0;
const pendingRequests = new Map();
// 添加请求到pending队列
const addPendingRequest = (config: any) => {
  const requestKey = `${config.method}-${config.url}-${JSON.stringify(config.params || config.data || {})}`;
  if (pendingRequests.has(requestKey)) {
    if (config.method?.toLowerCase() !== 'get') {
      const cancel = pendingRequests.get(requestKey);
      cancel('取消重复請求');
    }
    pendingRequests.delete(requestKey);
  }

  config.cancelToken = new CancelToken((cancel) => {
    pendingRequests.set(requestKey, cancel);
  });
};

// 移除请求从pending队列
const removePendingRequest = (config: any) => {
  const requestKey = `${config.method}-${config.url}-${JSON.stringify(config.params || config.data || {})}`;
  if (pendingRequests.has(requestKey)) {
    pendingRequests.delete(requestKey);
  }
};

// 取消所有pending请求
export const cancelAllRequests = () => {
  pendingRequests.forEach((cancel, key) => {
    cancel('页面卸载，取消請求');
    pendingRequests.delete(key);
  });
};

// 提供hooks用于组件卸载时取消请求
export const useRequestCancellation = () => {
  onBeforeUnmount(() => {
    cancelAllRequests();
  });
};
// 创建axios实例
const service = axios.create({
  timeout: 60 * 1000, // 请求超时时间
});
const ignoreErrMsgList = [
  '用戶已经綁定過此标签',
  '您無權限下线機器人',
  '该企业微信機器人已被綁定',
  '統一機器人还未激活接口许可',
  '{"code":10102,"msg":"order does not exist","tips":"訂單不存在"} (status: 404)',
  '無效訂單',
  '未激活服務商',
  '未上線第三方平台',
  '存在無效機器人: 機器人已被其它公海池綁定',
  '已存在相同名稱的用戶标签',
  '修改後的枚举元素數量不得小於修改前的枚举元素數量',
  '枚举单选數據类型的属性與規則輸入值不匹配',
  '渠道活碼配置的統一機器人未激活服務商',
  '綁定的負責人不在该團隊中',
  '綁定的團隊成員存在無效用戶',
  '場景自定義信息已开启，請配置訂單卡片信息後重新提交',
  '标签已存在',
  '此号已被移除出企微主體，請檢查後重试',
  '場景不存在',
  '機器人编号無效',
  '當前公海池用戶已分配接待人',
  '已存在相同名稱的渠道活碼',
  '企微宝平台不在線',
  '未购买接口许可',
  '该企微號已登錄，請勿重复登錄',
  '統一機器人已被其他客服綁定',
  '10分鐘内只能同步一次機器人信息，請稍後重试',
  '數據請求太過频繁，請稍後再试~ ',
  '無權限下线其它團隊機器人',
];
const errorHandler = (e: any, report = true) => {
  const res = e?.response?.data || e;
  const url = e?.response?.config?.url;
  // 兼容下载接口提示
  const downloadTipStr = url?.includes('/download/personal_dashboard') ? '下載太過频繁' : '';
  let showMsg = !(url?.includes('gpt.linghit') && res?.code > 1);
  if (res?.errcode === 401 || e?.response?.status === 401) {
    return loginError();
  } else {
    let msgStr = downloadTipStr || res?.tips || res?.msg || res?.errmsg || res?.message;
    // 部分提示不做上报
    if (ignoreErrMsgList.includes(msgStr) || ignoreErrMsgList.findIndex((v) => msgStr?.indexOf(v) !== -1) !== -1) {
      report = false;
    }
    showMsg && showErrMsg(msgStr);
    const rootStoreEx = rootStore();
    report &&
      errReport({
        type: 'api',
        message: msgStr || `${e?.name}: ${e?.message}`,
        request: {
          path: url,
          method: e.config?.method,
          headers: e.config?.headers,
          data: e.config?.data,
        },
        response: {
          status: e?.response?.status,
          data: e?.response?.data,
        },
        from: 'request.catch',
        target: `接口：${e.config.url}，触发用戶：${rootStoreEx?.currentUserInfo?.name}`,
      });
    throw e;
  }
};
const showErrMsg = (msgStr: string) => {
  isWechatBrowser ? Toast.fail(msgStr) : ElMessage.error(msgStr);
};

// request拦截器
service.interceptors.request.use(
  (config: any) => {
    addPendingRequest(config);
    const token = Cookies.get('IM-Token');
    const params = JSON.parse(Cookies.get('LINGDUN-HEADER-PARAMS') || '{}');
    config.headers['IM-Token'] = token;
    Object.keys(params).forEach((key) => {
      config.headers[key] = params[key];
    });

    return config;
  },
  (error) => {
    errorHandler(error);
  }
);
// response 拦截器
service.interceptors.response.use(
  (response) => {
    removePendingRequest(response.config);
    const res = response.data;
    if (res?.code) {
      if ([1, 200].includes(res.code)) {
        return res?.data;
      } else {
        let msgStr = res?.msg || res?.errmsg || res?.message || res?.ElMessage || '抱歉，系統錯誤';
        showErrMsg(msgStr);
        return Promise.reject(msgStr);
      }
    } else {
      // 响应体为数据，无code
      return res;
    }
  },
  (error) => {
    reqNum++;
    if (reqNum > 5) {
      reqNum = 0;
      checkVersion();
    }
    if (
      (error?.response?.status === 403 && error?.response?.data?.errcode === 403) ||
      [22055, 22054, 23023].includes(error?.response?.data?.code)
    ) {
      errorHandler(error, false);
    } else if (error.code !== 'ERR_CANCELED' && error.message !== 'canceled') {
      errorHandler(error);
    }
  }
);

const get = (url: string, params?: any, options: any = {}): any => {
  params = filterUselessParams(params);
  return service.get(url, {
    params,
    ...options,
    cancelToken: options.cancelFn ? new CancelToken(options.cancelFn) : undefined,
  });
};
const post = (url: string, body?: any, headers = {}, options: any = {}): any => {
  if (!(body instanceof FormData)) {
    body = filterUselessParams(body);
  }
  return service.post(url, body, {
    headers,
    ...options,
    cancelToken: options.cancelFn ? new CancelToken(options.cancelFn) : undefined,
  });
};
const remove = (url: string, body?: any) => {
  return service.delete(url, { data: body });
};
const put = (url: string, body: any, headers = {}) => {
  if (!(body instanceof FormData)) {
    body = filterUselessParams(body);
  }
  return service.put(url, body, {
    headers,
  });
};

export default {
  get,
  post,
  remove,
  put,
  cancelAllRequests,
  useRequestCancellation,
};
